import { useState } from 'react';
import {
  Briefcase,
  FileText,
  HardHat,
  Lock,
  Shield,
  Truck,
  UserCheck,
} from 'lucide-react';
import {
  ALL_CONTRACTS,
  computeContractSummary,
  CONTRACT_TYPES,
  extendExpiryByOneYear,
  generateContractId,
  todayDateString,
} from './contracts/mockContractData';
import ContractCreateForm from './contracts/ContractCreateForm';
import ContractLedgerTable from './contracts/ContractLedgerTable';

const CATEGORY_TABS = [
  { id: 'customer', label: 'Customer', icon: UserCheck },
  { id: 'transport', label: 'Transport', icon: Truck },
  { id: 'tankLease', label: 'Tank Lease', icon: Briefcase },
  { id: 'driverEmployment', label: 'Driver Employment', icon: HardHat },
  { id: 'insurance', label: 'Insurance', icon: Shield },
];

export default function ContractManagement() {
  const [activeTab, setActiveTab] = useState('customer');
  const [contracts, setContracts] = useState(ALL_CONTRACTS);
  const summary = computeContractSummary(contracts);
  const filteredByType = contracts.filter((c) => c.type === activeTab);

  const handleCreate = ({ type, partyName, slaTier, startDate, expiryDate }) => {
    const newContract = {
      id: generateContractId(type, contracts),
      type,
      partyName,
      slaTier,
      startDate,
      expiryDate,
      status: 'active',
    };
    setContracts((prev) => [newContract, ...prev]);
  };

  const handleRenew = (id) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === id && c.status === 'active'
          ? {
              ...c,
              startDate: c.expiryDate,
              expiryDate: extendExpiryByOneYear(c.expiryDate),
            }
          : c,
      ),
    );
  };

  const handleTerminate = (id, note) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              status: 'terminated',
              expiryDate: todayDateString(),
              terminationNote: note || 'Contract terminated by compliance officer',
            }
          : c,
      ),
    );
  };

  return (
    <section aria-label="Contract management compliance ledger" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-logi-grid pb-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 ring-1 ring-emerald-500/30">
              <FileText className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700/80">
              OGTS LogiCore™ · Secure Compliance Ledger
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-logi-silver/80 px-2 py-0.5 text-[10px] font-bold uppercase text-logi-muted ring-1 ring-logi-grid">
              <Lock className="h-2.5 w-2.5" />
              Encrypted
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-logi-text sm:text-2xl">
            Contract Management
          </h2>
          <p className="mt-1 text-sm text-logi-muted">
            Create · renew · terminate contracts · SLA tiers · Auto-alert renewal system
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="rounded-xl border border-logi-grid bg-white px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Total Contracts
            </p>
            <p className="font-mono text-lg font-bold text-logi-text">{summary.total}</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Urgent (&lt; 30d)
            </p>
            <p className="font-mono text-lg font-bold text-red-700">{summary.urgent}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Renewal Due (&lt; 90d)
            </p>
            <p className="font-mono text-lg font-bold text-amber-700">{summary.warning}</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Active
            </p>
            <p className="font-mono text-lg font-bold text-emerald-700">{summary.activeCount}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {CATEGORY_TABS.map((tab) => {
          const tabContracts = contracts.filter((c) => c.type === tab.id);
          const typeSummary = computeContractSummary(tabContracts);
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg border p-2.5 text-left transition-all ${
                activeTab === tab.id
                  ? 'border-emerald-500/40 bg-emerald-50 ring-1 ring-emerald-500/25'
                  : 'border-logi-grid bg-white hover:border-logi-grid hover:bg-logi-silver/80'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Icon
                  className={`h-3.5 w-3.5 ${activeTab === tab.id ? 'text-emerald-700' : 'text-logi-text0'}`}
                  strokeWidth={1.75}
                />
                <p className="truncate text-[10px] font-bold uppercase tracking-wide text-logi-muted">
                  {tab.label}
                </p>
              </div>
              <p className="mt-1 font-mono text-lg font-bold text-logi-text">{tabContracts.length}</p>
              {typeSummary.urgent > 0 && (
                <p className="text-[9px] font-semibold text-red-700">{typeSummary.urgent} urgent</p>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-logi-grid/80 bg-white px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
          Auto-Alert Legend
        </p>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-500/35">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Active · ≥ 90 days
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-amber-500/35">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          Renewal Due · &lt; 90 days
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-red-500/35">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
          Critical · &lt; 30 days
        </span>
      </div>

      <ContractCreateForm contractType={activeTab} onSubmit={handleCreate} />

      <div key={activeTab} className="animate-[fadeSlideIn_0.25s_ease-out]">
        <ContractLedgerTable
          contracts={filteredByType}
          contractType={activeTab}
          onRenew={handleRenew}
          onTerminate={handleTerminate}
        />
      </div>

      <p className="text-center text-[10px] text-logi-text0">
        {CONTRACT_TYPES[activeTab].label} · Secure ledger · Alerts computed daily at 06:00 GST
      </p>
    </section>
  );
}
